/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2017-2018, Bulb Inc.  For more information, please email contact@hellobulb.com.
 */

/**
 * Our wrapper to simplify the usage for the DataTables library (https://datatables.net/).
 */
angular.module('bulbApp.organizationModule')
  .directive('organizationDatatable', ['$compile',
    '$cookies',
    '$timeout',
    '$translate',
    'magnitudeFilter',
    organizationDatatable]);

function organizationDatatable($compile, $cookies, $timeout, $translate, magnitudeFilter) {
  return {
    scope: {},
    link: function link(scope, elem, attr) {
      const organizationId = attr.topOrganizationId;
      const tableContext   = attr.organizationDatatable;
      const tableId        = `${tableContext.toLowerCase()}-table`;

      const gradeMapping = {
        FIRST: '1', SECOND: '2', THIRD: '3', FOURTH: '4',
        FIFTH: '5', SIXTH: '6', SEVENTH: '7', EIGHTH: '8',
        NINTH: '9', TENTH: '10', ELEVENTH: '11', TWELFTH: '12',
      };

      // Kept in sync by the organizationGradeFilter directive via onFilterChange callback
      let selectedGrades = [];

      // ── Grade-filter directive (STUDENT table only) ─────────────────────
      // We compile an <organization-grade-filter> element into this directive's scope
      // so it shares the Angular lifecycle and gets destroyed together.
      const isStudentTable = tableContext === 'STUDENT';
      let gradeFilterEl = null;

      if (isStudentTable) {
        scope.onGradeFilterChange = function (grades) {
          selectedGrades = grades || [];
          elem.DataTable().draw();
        };

        gradeFilterEl = $compile(
          `<organization-grade-filter
              table-id="${tableId}"
              on-filter-change="onGradeFilterChange(grades)">
           </organization-grade-filter>`
        )(scope);

        // Append as a hidden host element right after the table in the DOM
        elem.after(gradeFilterEl);
      }

      $timeout(() => {
        const dataTablesSearchBar = $(`#${tableId}_filter input`);

        // Get the search bar placeholder working here.
        dataTablesSearchBar.attr(
          'placeholder',
          $translate.instant('organization_dashboard_table_search_placeholder'),
        );

        let searchDebounce = null;
        let searchValue;

        // We need to override the DataTables events here so we can input our own, mainly for the usage of the
        // debounce and so we can make it so if the user hits enter, they automatically search instead of
        // waiting for the debounce.
        dataTablesSearchBar.unbind();
        dataTablesSearchBar.keyup(function (e) {
          searchValue = this.value;

          if (searchDebounce) {
            $timeout.cancel(searchDebounce);
          }

          // This is the debounce, which will repeatedly get canceled until the user has stopped typing, or hits
          // the enter key.
          searchDebounce = $timeout(() => {
            elem.DataTable().search(searchValue).draw();
          }, 1000);

          // If they hit the enter key, we want to immediately search.
          if (e.keyCode === 13) {
            elem.DataTable().search(searchValue).draw();
            $timeout.cancel(searchDebounce);
          }
        });

        // Inject the grade-filter icon for the first time after the table renders
        if (isStudentTable) {
          gradeFilterEl[0].injectIcon();
        }
      });

      // ── Row hover helpers ───────────────────────────────────────────────
      function setUpTable() {
        const $rowsOnPage = $('[role="row"]').not('.bulb-dashboard-lower-table-first-row');

        // Set up the row events here.
        for (let i = 0; i < $rowsOnPage.length; i++) {
          const $currentRow = $($rowsOnPage[i]);

          $currentRow.find('td:first-child').mouseenter((e) => { // jshint ignore: line
            adjustRowToHover($(e.currentTarget), 'enter');
          });

          // Create an event for when the mouse leaves the row here.
          $currentRow.find('td:first-child').mouseleave((e) => { // jshint ignore: line
            adjustRowToHover($(e.currentTarget), 'leave');
          });
        }
      }

      // This function actually handles the event that is called each time one of the "td" are hovered over, or
      // the hover leaves the "td".
      function adjustRowToHover($childElement, event) {
        const $row = $childElement.parent();

        // Most of the work is done in the "highlighted-table-row" class, but there is no "column span" css
        // trick that works, since it's layout and not styling. If CSS ever adds column span, it can all be
        // done in the class. So we still need to change the colspan here.
        if (event === 'enter') {
          $row.addClass('highlighted-table-row');
          $row.children().first().attr('colspan', 4);
        } else {
          $row.removeClass('highlighted-table-row');
          $row.children().first().removeAttr('colspan');
        }
      }

      // ── Translations ────────────────────────────────────────────────────
      const translationArray = $translate.instant([
        'organization_dashboard_lower_table_info_organization',
        'organization_dashboard_lower_table_info_school',
        'organization_dashboard_lower_table_info_grade',
        'organization_dashboard_lower_table_info_class',
        'organization_dashboard_lower_table_info_admin',
        'organization_dashboard_lower_table_info_teacher',
        'organization_dashboard_lower_table_info_student',
        'organization_dashboard_lower_table_info_other',
        'organization_dashboard_lower_table_previous_button',
        'organization_dashboard_lower_table_next_button',
        'unit_abbrev_bytes',
        'grade_level_POSTGRADUATE',
      ]);

      // Checks and sets the translation to what should be used for this specific table.
      const currentTranslation = (tableContext === 'ADMINISTRATOR') ? 'organization_dashboard_lower_table_info_admin'
        : (tableContext === 'EDUCATOR')      ? 'organization_dashboard_lower_table_info_teacher'
          : (tableContext === 'STUDENT')     ? 'organization_dashboard_lower_table_info_student'
            : 'organization_dashboard_lower_table_info_other';

      // ── DataTable creation ──────────────────────────────────────────────
      const createTable = function (topOrganizationId) {
        // Define the columns here, this will set up the table according to the DataTables library.
        const columnDefs = [
          {
            name: 'firstName',
            width: '30%',
            targets: 0,
            searchable: false,
            render(data, type, full) {
              const canOffboard = full.connections.clever == null && full.connections.classLink == null;
              return `<span>${full.firstName}</span>
                  <dashboard-account-hover
                    name="${full.username}"
                    org-id="${topOrganizationId}"
                    can-offboard="${canOffboard}">
                  </dashboard-account-hover>`;
            },
          },
          {
            name: 'lastName',
            width: '30%',
            targets: 1,
            searchable: false,
            render(data, type, full) { return full.lastName; },
          },
          {
            name: 'grade',
            targets: 2,
            searchable: false,
            orderable: isStudentTable,
            render(data, type, full) {
              if (gradeMapping[full.grade]) return gradeMapping[full.grade];
              return (full.grade === 'POSTGRADUATE'
                ? translationArray.grade_level_POSTGRADUATE
                : full.grade) || '-';
            },
          },
          {
            name: 'metrics.numPublishedPages',
            targets: 3,
            searchable: false,
            render(data, type, full) { return full.metrics.numPublishedPages; },
          },
          {
            name: 'searchString',
            targets: 4,
            visible: false,
            searchable: true,
            render(data, type, full) {
              let searchString = `${full.username} ${full.defaultEmail}`;

              // This for loop creates a string of emails that are searchable by DataTables, but are not
              // visible to anyone. It's used only for searching purposes.
              for (let i = 0; i < full.emails.length; i++) {
                if (full.emails[i].verified === true) {
                  searchString += (` ${full.emails[i].email}`);
                }
              }
              return searchString;
            },
          },
        ];
        // Set the table initialization variables with the proper translations.
        /* jshint camelcase: false */
        return elem.DataTable({
          createdRow(row, data) {
            $compile(angular.element(row).contents())(scope);
            $(row).attr('id', data.username);
            $(row).attr('role', 'row');
          },
          columnDefs,
          processing: true,
          serverSide: true,
          ajax(dtData, callback, settings) {
            let url = `/b/rest/organizations/${topOrganizationId}/members?position=${tableContext}`;
            if (isStudentTable && selectedGrades.length > 0) {
              url += `&grade=${encodeURIComponent(selectedGrades.join(','))}`;
            }

            // Strip DataTables-internal fields the server doesn't need
            dtData.columns = dtData.columns.map((columnData) => {
              delete columnData.searchable;
              delete columnData.orderable;
              delete columnData.search;
              return columnData;
            });

            $.ajax({
              url,
              type: 'POST',
              contentType: 'application/json',
              dataType: 'json',
              headers: { 'X-XSRF-TOKEN': $cookies.get('XSRF-TOKEN') },
              data: JSON.stringify(dtData),
              success: callback,
              error() {
                // Surface error to DataTables so it shows its standard error message
                callback({ draw: dtData.draw, recordsTotal: 0, recordsFiltered: 0, data: [] });
              },
            });
          },
          autoWidth: false,
          lengthChange: false,
          pagingType: 'full_numbers',
          pageLength: 20,
          order: [[0, 'asc']],
          language: {
            paginate: {
              first: '&laquo;', // "<<" symbol
              last: '&raquo;', // ">>" symbol
              previous: translationArray.organization_dashboard_lower_table_previous_button,
              next: translationArray.organization_dashboard_lower_table_next_button,
            },
            info: translationArray[currentTranslation],
          },
        });
      };

      const thisTable = createTable(organizationId);
      setUpTable();

      // Add an event listener here for when the table is re-drawn, this means using the pagination, sorting, or
      // searching. Mainly so that the function up above that creates the hover effects is reapplied each time.
      thisTable.on('draw', () => {
        setUpTable();
        // Re-inject the grade filter icon after every draw (DataTables recreates thead on draw)
        if (isStudentTable) {
          gradeFilterEl[0].injectIcon();
        }
      });

      // When the table is destroyed, activate clear() and destroy() on it, just to make sure.
      elem.bind('$destroy', () => {
        elem.DataTable().clear().destroy();
      });
    },
  };
}
